"use client";

import { CaretRight } from "@phosphor-icons/react";
import type { ComponentProps, ReactNode } from "react";
import { createContext, memo, useContext, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useControllableState } from "@/hooks/use-controllable-state";
import { Icon } from "@/lib/icon";
import { cn } from "@/lib/utils";

interface ChainOfThoughtContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const ChainOfThoughtContext = createContext<ChainOfThoughtContextValue | null>(
  null,
);

const useChainOfThought = () => {
  const context = useContext(ChainOfThoughtContext);
  if (!context) {
    throw new Error(
      "ChainOfThought components must be used within ChainOfThought",
    );
  }
  return context;
};

export type ChainOfThoughtProps = ComponentProps<"div"> & {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

// Patched from upstream: one Collapsible at the root, not two split
// instances in Header and Content. Upstream's split broke Base UI's
// internal trigger↔panel ARIA linkage — the trigger's aria-controls
// couldn't reference a panel that lived in a sibling Collapsible root.
// One root means descendants share state via Base UI context and the
// linkage works for screen readers.
export const ChainOfThought = memo(
  ({
    className,
    open,
    defaultOpen = false,
    onOpenChange,
    children,
    ...props
  }: ChainOfThoughtProps) => {
    const [isOpen, setIsOpen] = useControllableState({
      defaultProp: defaultOpen,
      onChange: onOpenChange,
      prop: open,
    });

    const chainOfThoughtContext = useMemo(
      () => ({ isOpen: !!isOpen, setIsOpen }),
      [isOpen, setIsOpen],
    );

    return (
      <ChainOfThoughtContext.Provider value={chainOfThoughtContext}>
        <Collapsible
          className={cn("not-prose flex w-full flex-col gap-2 p-4", className)}
          onOpenChange={setIsOpen}
          open={!!isOpen}
          {...props}
        >
          {children}
        </Collapsible>
      </ChainOfThoughtContext.Provider>
    );
  },
);

export type ChainOfThoughtHeaderProps = ComponentProps<
  typeof CollapsibleTrigger
>;

export const ChainOfThoughtHeader = memo(
  ({ className, children, ...props }: ChainOfThoughtHeaderProps) => {
    const { isOpen } = useChainOfThought();

    return (
      <CollapsibleTrigger
        className={cn(
          "flex w-full items-center gap-2 text-left font-medium text-muted-foreground text-sm transition-colors hover:text-foreground",
          className,
        )}
        {...props}
      >
        <Icon
          icon={CaretRight}
          className={cn(
            "size-3 shrink-0 transition-transform",
            isOpen ? "rotate-90" : "rotate-0",
          )}
        />
        <span className="flex-1">{children ?? "Chain of Thought"}</span>
      </CollapsibleTrigger>
    );
  },
);

export type ChainOfThoughtStepProps = ComponentProps<"div"> & {
  /**
   * Override the dot indicator. Default renders an 8px circle styled per
   * `status`: complete → `bg-foreground`, active → `bg-ring` (Lua purple),
   * pending → hollow `border-border`.
   */
  icon?: ReactNode;
  label: ReactNode;
  description?: ReactNode;
  status?: "complete" | "active" | "pending";
};

const dotStyles = {
  complete: "bg-foreground",
  active: "bg-ring",
  pending: "border border-border bg-background",
};

const labelStyles = {
  complete: "text-foreground",
  active: "text-foreground",
  pending: "text-muted-foreground",
};

export const ChainOfThoughtStep = memo(
  ({
    className,
    icon,
    label,
    description,
    status = "complete",
    children,
    ...props
  }: ChainOfThoughtStepProps) => (
    <div
      className={cn(
        "fade-in-0 slide-in-from-top-2 flex animate-in items-start gap-3 px-2 pt-2",
        className,
      )}
      {...props}
    >
      {/* Indicator column: dot + vertical connector */}
      <div className="flex shrink-0 flex-col items-center gap-1 self-stretch pt-1.5">
        {icon ?? (
          <div
            aria-hidden
            className={cn("size-2 shrink-0 rounded-full", dotStyles[status])}
          />
        )}
        <div className="min-h-px w-0.5 flex-1 rounded-[1px] bg-border" />
      </div>
      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 pb-4">
        <div className={cn("font-medium text-sm", labelStyles[status])}>
          {label}
        </div>
        {description && status !== "pending" && (
          <div className="font-normal text-muted-foreground text-sm">
            {description}
          </div>
        )}
        {children}
      </div>
    </div>
  ),
);

export type ChainOfThoughtSearchResultsProps = ComponentProps<"div">;

export const ChainOfThoughtSearchResults = memo(
  ({ className, ...props }: ChainOfThoughtSearchResultsProps) => (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      {...props}
    />
  ),
);

export type ChainOfThoughtSearchResultProps = ComponentProps<typeof Badge>;

export const ChainOfThoughtSearchResult = memo(
  ({ className, children, ...props }: ChainOfThoughtSearchResultProps) => (
    <Badge
      className={cn("gap-1 px-2 py-0.5 font-normal text-xs", className)}
      variant="secondary"
      {...props}
    >
      {children}
    </Badge>
  ),
);

export type ChainOfThoughtContentProps = ComponentProps<
  typeof CollapsibleContent
>;

export const ChainOfThoughtContent = memo(
  ({ className, children, ...props }: ChainOfThoughtContentProps) => (
    <CollapsibleContent
      className={cn(
        "flex flex-col text-popover-foreground outline-none",
        "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 data-[state=closed]:animate-out data-[state=open]:animate-in",
        className,
      )}
      {...props}
    >
      {children}
    </CollapsibleContent>
  ),
);

export type ChainOfThoughtImageProps = ComponentProps<"div"> & {
  caption?: string;
};

export const ChainOfThoughtImage = memo(
  ({ className, children, caption, ...props }: ChainOfThoughtImageProps) => (
    <div className={cn("mt-2 space-y-2", className)} {...props}>
      <div className="relative flex max-h-[22rem] items-center justify-center overflow-hidden rounded-lg bg-muted p-3">
        {children}
      </div>
      {caption && <p className="text-muted-foreground text-xs">{caption}</p>}
    </div>
  ),
);

ChainOfThought.displayName = "ChainOfThought";
ChainOfThoughtHeader.displayName = "ChainOfThoughtHeader";
ChainOfThoughtStep.displayName = "ChainOfThoughtStep";
ChainOfThoughtSearchResults.displayName = "ChainOfThoughtSearchResults";
ChainOfThoughtSearchResult.displayName = "ChainOfThoughtSearchResult";
ChainOfThoughtContent.displayName = "ChainOfThoughtContent";
ChainOfThoughtImage.displayName = "ChainOfThoughtImage";
